import { useState, useEffect } from "react";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { Box, Paper, Typography, Tooltip } from "@mui/material";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FolderRounded from "@mui/icons-material/FolderRounded";
import ArticleIcon from "@mui/icons-material/Article";
import ImageIcon from "@mui/icons-material/Image";
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';

import { transformFilesToTree } from "../../utils/utils.service";
import './FileTree.css';
import { FileApiToFetchFolder, FileNode } from "../../interfaces/api.interface";

interface FileTreeProps {
  files: { file: string; url: string }[];
  networkPath: string;
  codeCabinet: string;
  initExpandedItems: boolean;
  onSelect: (file: FileApiToFetchFolder) => void;
}

const FileTree: React.FC<FileTreeProps> = ({ files, networkPath, codeCabinet, initExpandedItems, onSelect }) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [treeData, setTreeData] = useState<FileNode | null >(null);
  const colorIcon = '#424242';
  
  useEffect(() => {
    const newExpandedItems: string[] = [];

    const collectExpandedItems = (node: FileNode, parentPath = "") => {
      const currentPath = parentPath ? `${parentPath}/${node.file}` : node.file;
      newExpandedItems.push(currentPath);
      node.children?.forEach(child => collectExpandedItems(child, currentPath));
    };

    const treeData = transformFilesToTree(files, networkPath, codeCabinet);
    
    setTreeData(treeData)
    collectExpandedItems(treeData);
    setExpandedItems(prev => [...prev, treeData.file]);
    if(initExpandedItems){
      setExpandedItems([]);
    }

  }, [files, networkPath, codeCabinet, initExpandedItems]);

  const handleToggle = (itemId: string) => {
    setExpandedItems(prevExpandedItems => 
      prevExpandedItems.includes(itemId)
        ? prevExpandedItems.filter(id => id !== itemId)
        : [...prevExpandedItems, itemId]
    );
  };

  const getIconForFile = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return <PictureAsPdfIcon sx={{ mr: 1, color: colorIcon }} />;
      case 'doc':
      case 'docx':
        return <ArticleIcon sx={{ mr: 1, color: colorIcon }} />;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <ListAltOutlinedIcon sx={{ mr: 1, color: colorIcon }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <ImageIcon sx={{ mr: 1, color: colorIcon }} />;
      case 'msg':
        return <MailOutlinedIcon sx={{ mr: 1, color: colorIcon }} />;
      default:
        return <InsertDriveFileIcon sx={{ mr: 1, color: colorIcon }} />;
    }
  };

  const renderTree = (node: FileNode, parentPath = ""): React.ReactNode => {
    const currentPath = parentPath ? `${parentPath}/${node.file}` : node.file;
    const isFolder = !node.url;

    return (
      <TreeItem
        key={currentPath}
        itemId={currentPath}
        label={
          <Tooltip title={node.file} arrow sx={{ '&:hover': {
            backgroundColor: 'lightgray', color: 'blue'
                          }}}>
            <Box sx={{ display: 'flex', alignItems: 'center'}}>
              {isFolder ? (
                <FolderRounded sx={{ mr: 1, color: colorIcon }} className="folder" />
              ) : (
                getIconForFile(node.file)
              )}
              <Typography sx={{fontSize: '0.9rem' }}>{node.file.split("/").pop() || node.file}</Typography>
            </Box>
          </Tooltip>
        }
        onClick={() => {
          handleToggle(currentPath);
          onSelect(node);
        }}

      >
        {node.children && node.children.length > 0 && node.children.map((child) => renderTree(child, currentPath))}
      </TreeItem>
    );
  };
  
  return (
    <>
      {files.length > 0 && (
        <Paper className="file-tree">
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Typography sx={{ bgcolor: '#e0e0e0', padding: '10px' }}>Arborescence</Typography>
            <SimpleTreeView
              aria-label="file tree"
              sx={{ height: "100%", flexGrow: 1, overflowY: "auto", width: "100%" }}
              expandedItems={expandedItems}
            >
              {renderTree(treeData as FileNode)}
            </SimpleTreeView>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default FileTree;
